import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import routes from "@/routes";

import {
  Button,
  Flex,
  Card,
  App,
  Spin,
  Descriptions,
  Modal,
  Tag,
  Row,
  Col,
  Avatar,
  Tooltip,
  Typography,
} from "antd";

import EditIcon from "@/icons/EditIcon";
import TrashIcon from "@/icons/TrashIcon";
import ExternalLinkIcon from "@/icons/ExternalLinkIcon";
import UserIcon from "@/icons/UserIcon";

import EmployeeModal from "@/components/modals/EmployeeModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import BackBtn from "@/components/ui/BackBtn";
import CopyableText from "@/components/ui/СopyableText";
import UnitsTable from "@/components/tables/UnitsTable";
import BindingUserModal from "@/components/modals/BindingUserModal";

import { getItem, deleteItem, getEmployeeUnits } from "@/api/employees";
import { getUsersOptions, unbindingEmployee } from "@/api/users";
import { getUnitsOptions } from "@/api/units";
import { getPositionsOptions } from "@/api/positions";
import { getFullName, formateDate } from "@/utils";

import { useEmployees } from "@/context/EmployeesContext";

const { Title } = Typography;

const Employee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Данные сотрудника
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [employeeUnits, setEmployeeUnits] = useState([]);

  const [units, setUnits] = useState([]);
  const [positions, setPositions] = useState([]);

  const [usersList, setUsersList] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);

  const { employees } = useEmployees();

  const fio = useMemo(() => {
    if (!data || !data.employee) return "";
    return getFullName(
      data.employee.lastname,
      data.employee.firstname,
      data.employee.patronymic
    );
  }, [data]);

  const initials = useMemo(() => {
    if (!data || !data.employee) return "";
    return `${data.employee.lastname?.[0] ?? ""}${
      data.employee.firstname?.[0] ?? ""
    }`;
  }, [data]);

  const isDeleted = useMemo(() => {
    return data?.employee?.deleted;
  }, [data]);

  const status = employees.find(
    (emp) => emp.employee_uuid === data?.employee?.uuid
  )?.status;

  // initial fetch
  useEffect(() => {
    fetchData();
    fetchOptions();
    // eslint-disable-next-line
  }, [id]);

  // Запрос на сервер для получения данных
  const fetchData = async () => {
    setIsLoading(true);

    try {
      const [data, employeeUnits] = await Promise.all([
        getItem(id),
        getEmployeeUnits(id),
      ]);

      setData(data);
      setEmployeeUnits(employeeUnits);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const [{ users }, { units }, { positions }] = await Promise.all([
        getUsersOptions(),
        getUnitsOptions(),
        getPositionsOptions({ deleted: false }),
      ]);

      setUsersList(
        users.map((item) => {
          return {
            ...item,
            label: `${item.email}`,
          };
        })
      );

      setUnits(units.filter((unit) => unit.unit_type?.is_hierarchical));
      setPositions(positions);
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = () => {
    setShowModal(false);
    fetchData();
  };

  const handleUnbindingUser = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите отвязать пользователя ${data.user.email} от сотрудника ${fio}?`,
      content: "",
      onOk() {
        handleConfirmUnbinding();
      },
      okText: "Отвязать",
      cancelText: "Отмена",
    });
  };

  const handleConfirmUnbinding = async () => {
    setIsLoading(true);
    try {
      await unbindingEmployee({ user_uuid: data.user.uuid });
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    const { firstname, lastname } = data.employee;

    Modal.confirm({
      title: `Вы уверены, что хотите удалить сотрудника ${firstname} ${lastname}?`,
      content:
        "Удаление сотрудника приведет к его исключению из списка сотрудников и он больше не будет отображаться в системе.",
      onOk() {
        handleConfirmDelete();
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем сотрудника после подтверждения
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: data.employee.uuid });
      navigate(routes.administration.employeeList);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} align="flex-start">
            <Flex vertical gap={10} style={{ width: "100%" }}>
              <Breadcrumbs
                items={[
                  {
                    title: "Административная панель",
                  },
                  {
                    title: "Сотрудники",
                    href: routes.administration.employeeList,
                  },
                  {
                    title: "Информация о сотруднике",
                  },
                ]}
              />

              <Flex
                align="center"
                justify="space-between"
                gap={20}
                style={{ width: "100%" }}
              >
                <BackBtn>Информация о сотруднике</BackBtn>

                {!isDeleted && (
                  <Button
                    type="primary"
                    icon={<EditIcon />}
                    onClick={() => setShowModal(true)}
                  ></Button>
                )}
              </Flex>
            </Flex>

            {/* Раздел: Данные Сотрудника */}
            <Card>
              <Row gutter={24}>
                <Col xs={24} sm={8} md={8}>
                  <Flex vertical align="start" gap={10}>
                    <Tooltip
                      title={
                        status === "online"
                          ? "В сети"
                          : status === "inactive"
                          ? "Неактивен более 2 минут"
                          : status === "offline"
                          ? "Не в сети"
                          : ""
                      }
                      placement="bottomLeft"
                    >
                      <div style={{ position: "relative" }}>
                        <Avatar
                          size={48}
                          src={status ? data.employee.avatar : null}
                          style={{
                            color: status ? "#E66A25" : "#777777",
                            background: status ? "#FFEADE" : "#E9E9E9",
                            border: "none",
                            textTransform: "uppercase",
                          }}
                        >
                          {status ? initials : <UserIcon />}
                        </Avatar>

                        {(status === "online" || status === "inactive") && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "-1px",
                              right: "-1px",
                              width: 48 / 3,
                              height: 48 / 3,
                              border: "1px solid #fff",
                              borderRadius: "50%",
                              backgroundColor:
                                status === "online" ? "#2DAA55" : "#BBBBBB",
                            }}
                          />
                        )}
                      </div>
                    </Tooltip>

                    <div style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>
                      {fio}
                    </div>

                    {isDeleted && <Tag color="red">Удален</Tag>}
                  </Flex>
                </Col>

                <Col
                  xs={24}
                  sm={16}
                  md={16}
                  style={{
                    borderLeft: "1px solid #e9e9e9",
                    paddingLeft: "25px",
                  }}
                >
                  <Descriptions
                    column={1}
                    styles={{
                      label: { display: "inline-block", minWidth: "160px" },
                      content: {
                        display: "block",
                        marginLeft: "8px",
                        fontSize: 13,
                      },
                    }}
                  >
                    <Descriptions.Item label="ФИО">
                      <CopyableText text={fio} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Дата рождения">
                      {formateDate(data.employee.birthdate) || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Табельный номер">
                      {data.employee.table_number ? (
                        <CopyableText text={data.employee.table_number} />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Контактные данные">
                      {data.employee.phone ? (
                        <CopyableText text={data.employee.phone} />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="ИНН">
                      {data.employee.inn ? (
                        <CopyableText text={data.employee.inn} />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Документ">
                      {data.employee.document || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Подразделение">
                      {data.employee.unit?.name || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Должность">
                      {data.employee.position?.name || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Дата приема на работу">
                      {formateDate(data.employee.employmentdate) || "-"}
                    </Descriptions.Item>
                    {data.employee.dismissaldate && (
                      <Descriptions.Item label="Дата увольнения">
                        {formateDate(data.employee.dismissaldate)}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Раздел: Данные пользователя */}
            <Flex vertical gap={10}>
              <Title level={4} className="no-margin">
                Пользователь
              </Title>
              {data.user ? (
                <Card>
                  <Flex justify="space-between" style={{ width: "100%" }}>
                    <Flex vertical align="start" gap={20}>
                      <Descriptions
                        column={1}
                        styles={{
                          label: { display: "inline-block", minWidth: "160px" },
                          content: {
                            display: "block",
                            marginLeft: "8px",
                            fontSize: 13,
                          },
                        }}
                      >
                        <Descriptions.Item label="Логин">
                          {data.user.email ? (
                            <CopyableText text={data.user.email} />
                          ) : (
                            "-"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Роль">
                          <Tag>{data.user.role}</Tag>
                        </Descriptions.Item>
                      </Descriptions>
                      <Button onClick={handleUnbindingUser}>
                        Отвязать от сотрудника
                      </Button>
                    </Flex>

                    <Tooltip title="Перейти на страницу пользователя">
                      <Button
                        icon={<ExternalLinkIcon />}
                        color="default"
                        variant="link"
                        href={routes.administration.userDetail(data.user.uuid)}
                      ></Button>
                    </Tooltip>
                  </Flex>
                </Card>
              ) : (
                <Button onClick={() => setShowUsersModal(true)}>
                  Привязать к сотруднику
                </Button>
              )}
            </Flex>

            {/* Раздел: Рабочие группы */}

            <Flex vertical gap={10} style={{ width: "100%" }}>
              <Title level={4} className="no-margin">
                Рабочие группы
              </Title>

              <UnitsTable
                data={employeeUnits}
                pagination={false}
                showActions={false}
              />
            </Flex>

            {!isDeleted && (
              <Button
                danger
                type="link"
                icon={<TrashIcon />}
                onClick={handleDelete}
                style={{ paddingInline: 0 }}
              >
                Удалить
              </Button>
            )}
          </Flex>

          {showModal && (
            <EmployeeModal
              data={data.employee}
              units={units}
              positions={positions}
              visible={showModal}
              onCancel={() => setShowModal(false)}
              onSave={handleUpdate}
            />
          )}

          {showUsersModal && (
            <BindingUserModal
              visible={showUsersModal}
              list={usersList}
              employee_uuid={data?.employee?.uuid}
              onSave={() => {
                setShowUsersModal(false);
                fetchData();
              }}
              onCancel={() => setShowUsersModal(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Employee;
